/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { UserState } from '../user-state';
import { UserInfo } from 'src/app/core/models/entites/userInfo';
import {
  RESET_AUTH_ERROR_MESSAGE,
  USER_LOGIN,
  USER_LOGIN_FAILED,
  USER_LOGIN_SUCCESS,
  USER_REGISTRATION,
  USER_REGISTRATION_FAILED,
  USER_REGISTRATION_RESULT
} from '../actions/user-authentification.actions';
import {
  MARK_ONE_NOTIFICATION_AS_READ,
  MARK_ALL_NOTIFICATIONS_AS_READ,
  SET_UNREAD_NOTIFICATIONS_COUNT,
  UPDATE_POINTS_COUNT
} from '../actions/user.actions';

const getUserFeatureState = createFeatureSelector<UserState>('user');

export const selectUnreadNotificationCount = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => {
    return userInfo && userInfo.unreadNotificationCount! > 99
      ? '99+'
      : (userInfo && userInfo.unreadNotificationCount?.toString()) || '0';
  }
);

export const selectUnreadNotificationCountValue = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo?.unreadNotificationCount ?? 0
);

export const selectUserBalanceInMBC = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.pointsCount
);

export const selectUserBarCode = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.barCode
);

export const selectClientId = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.client_ID
);

export const selectUserMaxbetId = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.maxbet_ID
);

export const selectUserUuid = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.uuid
);

export const selectUserInfo = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo
);

export const selectUserFullName = createSelector(
  selectLocalStorageValue,
  (userInfo: UserInfo) => userInfo && userInfo.firstName + ' ' + userInfo.lastName
);

export const selectAuthError = createSelector(
  getUserFeatureState,
  (state: UserState) => state && state.authValidationError
);

const initialState: UserState = {
  user: new UserInfo('', '', '', '', '', '', '', 0, 0, '', '', 0, '', 0),
  authValidationError: ''
};

export const userReducer = createReducer<UserState>(
  initialState,
  on(UPDATE_POINTS_COUNT, (state: UserState, { pointsCount }): UserState => {
    const userInfo = selectLocalStorageValue();
    userInfo.pointsCount = pointsCount;
    localStorage.setItem('UserInfo', JSON.stringify(userInfo));
    return {
      ...state
    };
  }),
  on(USER_LOGIN_SUCCESS, (state: UserState, { loggedUser }): UserState => {
    const updatedUser = {
      ...loggedUser
    };
    localStorage.setItem('UserInfo', JSON.stringify(updatedUser));
    return {
      ...state,
      user: updatedUser
    };
  }),
  on(USER_LOGIN_FAILED, (state: UserState, { response }): UserState => {
    return {
      ...state,
      authValidationError:
        response.status === 0
          ? 'Održavanje u toku, molimo te da pokušaš kasnije.'
          : response.error.message
    };
  }),
  on(USER_LOGIN, (state: UserState): UserState => {
    return {
      ...state,
      authValidationError: ''
    };
  }),
  on(USER_REGISTRATION_RESULT, (state: UserState, { response }): UserState => {
    return {
      ...state,
      authValidationError:
        response.status === 0
          ? 'Održavanje u toku, molimo te da pokušaš kasnije.'
          : response.message
    };
  }),
  on(USER_REGISTRATION_FAILED, (state: UserState, { response }): UserState => {
    return {
      ...state,
      authValidationError:
        response.status === 0 ? 'Održavanje u toku, molimo te da pokušaš kasnije.' : response.error
    };
  }),
  on(USER_REGISTRATION, (state: UserState): UserState => {
    return {
      ...state
    };
  }),
  on(RESET_AUTH_ERROR_MESSAGE, (state: UserState): UserState => {
    return {
      ...state,
      authValidationError: ''
    };
  }),

  on(MARK_ALL_NOTIFICATIONS_AS_READ, (state: UserState): UserState => {
    const userInfo = selectLocalStorageValue();
    userInfo.unreadNotificationCount = 0;
    localStorage.setItem('UserInfo', JSON.stringify(userInfo));
    return {
      ...state
    };
  }),
  on(MARK_ONE_NOTIFICATION_AS_READ, (state: UserState): UserState => {
    const userInfo = selectLocalStorageValue();
    userInfo.unreadNotificationCount = Math.max(0, (userInfo.unreadNotificationCount || 1) - 1);
    localStorage.setItem('UserInfo', JSON.stringify(userInfo));
    return {
      ...state
    };
  }),
  on(SET_UNREAD_NOTIFICATIONS_COUNT, (state: UserState, { unreadNotificationCount }): UserState => {
    const userInfo = selectLocalStorageValue();
    userInfo.unreadNotificationCount = unreadNotificationCount;
    localStorage.setItem('UserInfo', JSON.stringify(userInfo));
    return {
      ...state
    };
  })
);

function selectLocalStorageValue(): UserInfo {
  const userInfo = JSON.parse(localStorage.getItem('UserInfo')!) as UserInfo;
  return userInfo;
}
